// Shared minimap types + helpers for the conversation outline. The timeline
// rendering lives in MinimapTimeline (used by the persistent dock at >= xl and
// by MinimapBottomSheet below xl); this module keeps the data shape
// (MinimapItem) and the pure helpers (groupIntoTurns, heuristicLabel) both
// consume.

export interface MinimapItem {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** LLM-generated short label; `null` while the ARQ job is pending or
   * terminally failed. Consumers fall back to a heuristic. */
  minimap_label: string | null;
  /** ISO timestamp of the message. Used by the timeline to show the turn's
   * time. Optional/nullable because live (not-yet-persisted) turns may not
   * carry one. */
  created_at?: string | null;
}

const FALLBACK_MAX_CHARS = 50;

/** Drop the trailing partial word and append `…` so the fallback reads
 * naturally even when truncation cuts mid-word. */
export function heuristicLabel(content: string, max: number = FALLBACK_MAX_CHARS): string {
  const trimmed = content.trim();
  if (trimmed.length <= max) return trimmed;
  const slice = trimmed.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  const head = lastSpace > max / 2 ? slice.slice(0, lastSpace) : slice;
  return `${head.replace(/[\s.,;:!?]+$/, "")}…`;
}

/** Group the flat message list into conversation turns (Q/A pairs).
 * Each `user` message opens a new turn; subsequent `assistant` messages
 * join the open turn. A leading assistant with no preceding user (a
 * defensive edge case) gets its own turn so no message is dropped. */
export function groupIntoTurns(items: MinimapItem[]): MinimapItem[][] {
  const turns: MinimapItem[][] = [];
  for (const it of items) {
    const open = turns.at(-1);
    if (it.role === "user" || open === undefined) {
      turns.push([it]);
    } else {
      open.push(it);
    }
  }
  return turns;
}
